import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Statistic,
  Space,
  Tooltip,
  Divider,
  Image,
} from "antd";
import CommonGraph from "./CommonGraph";
import vitalsIcon from "../../../../../assets/images/nurseIcons/heart-rate.svg";
import { useNavigate } from "react-router-dom";
import useQuery from "../../../../../hooks/useQuery";
import { useContext } from "react";
import { UserContext } from "../../../../../context/UserContext";
import useIsMobile from "../../../../../hooks/useIsMobile";

const Graphs = ({ data }) => {
  const navigate = useNavigate();
  const params = useQuery();
  const [noData, setNoData] = useState(false);
  const [activeX, setActiveX] = useState(null);
  const { userDetails } = useContext(UserContext);
  const role = userDetails?.currRole?.toLowerCase()?.trim();
  const isMobile = useIsMobile();
  const isSummaryMobile =
    isMobile && (role?.includes("doctor") || role?.includes("nurse"));

  const graphHeadings = [
    {
      heading: "Pulse Rate",
      graphData: data?.pulse_rate,
      lineColor: "#4169E1",
    },
    {
      heading: "Systolic BP",
      graphData: data?.["systolic BP"],
      lineColor: "#FF6977",
    },
    {
      heading: "Diastolic BP",
      graphData: data?.["diastolic BP"],
      lineColor: "#2DB181",
    },
    {
      heading: "Temp",
      graphData: data?.temperature,
      lineColor: "#7F56D9",
    },
    {
      heading: "Respiratory Care",
      graphData: data?.respiratory_rate,
      lineColor: "#FDD330",
    },
    {
      heading: "SpO2",
      graphData: data?.oxygen_saturation,
      lineColor: "#31A7FB",
    },
  ];

  useEffect(() => {
    const checkGraphData = graphHeadings.every(
      (item) => item.graphData?.length > 0
    );

    const gcsScalesEmpty =
      (!data?.gcs_scales?.eye_opening_response ||
        data.gcs_scales.eye_opening_response === "0") &&
      (!data?.gcs_scales?.verbal_response ||
        data.gcs_scales.verbal_response === "0") &&
      (!data?.gcs_scales?.motor_response ||
        data.gcs_scales.motor_response === "0");

    const inputOutputEmpty =
      data?.["input/output"]?.input === 0 &&
      data?.["input/output"]?.output === 0 &&
      data?.["input/output"]?.balance === 0;

    setNoData(
      !checkGraphData &&
        gcsScalesEmpty &&
        inputOutputEmpty &&
        data?.grbs?.length === 0
    );
  }, [data]);

  const balance =
    data?.["input/output"]?.input - data?.["input/output"]?.output || 0;

  const navigateToIoCharts = () => {
    if (role?.includes("nurse")) {
      navigate(
        `/nurse/home?status=${params?.status}&patient=${params?.patient}&type=monitoring-charts`
      );
    } else {
      navigate(
        `/doctor/home?patient-type=${params?.["patient-type"]}&status=${params?.status}&patient=${params?.patient}&type=monitoring-charts`
      );
    }
  };

  const renderIoBalanceCard = (mobileHead = false) => (
    <Card
      style={{ height: "100%" }}
      size="small"
      className={`tiny-graph-card${
        mobileHead ? " landing-io-balance-card" : ""
      }`}
      title={
        mobileHead ? (
          <div className="landing-io-balance-mobile-head">
            <Typography onClick={navigateToIoCharts} style={{ cursor: "pointer" }}>
              IO Balance
            </Typography>
            <Statistic
              value={balance}
              valueStyle={{
                color: balance < 0 ? "#FF4D4F" : "#52C41A",
                fontWeight: 600,
                fontSize: "24px",
              }}
            />
          </div>
        ) : (
          <Row>
            <Col span={12}>
              <Typography onClick={navigateToIoCharts} style={{ cursor: "pointer" }}>
                IO Balance
              </Typography>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Statistic
                value={balance}
                valueStyle={{
                  color: balance < 0 ? "#FF4D4F" : "#52C41A",
                  fontWeight: 600,
                  fontSize: "24px",
                }}
              />
            </Col>
          </Row>
        )
      }
    >
      <Typography style={{ textAlign: "center" }}>Input / Output</Typography>
      <Space
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {data?.["input/output"]?.input && data?.["input/output"]?.output && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              style={{
                color: "#52C41A",
                fontWeight: 600,
                fontSize: "24px",
                marginRight: 4,
              }}
            >
              {data?.["input/output"]?.input}
            </Typography>
            <Typography style={{ fontSize: "24px", marginHorizontal: 4 }}>
              /
            </Typography>
            <Typography
              style={{
                color: "#FF4D4F",
                fontWeight: 600,
                fontSize: "24px",
                marginLeft: 4,
              }}
            >
              {data?.["input/output"]?.output}
            </Typography>
          </div>
        )}
      </Space>
    </Card>
  );

  const renderGcsCard = () => (
    <Card
      size="small"
      className="tiny-graph-card landing-gcs-card"
      style={{ height: "100%" }}
      title={<Typography>GCS</Typography>}
    >
      <Space
        size="small"
        className="landing-gcs-scores"
        style={{
          textAlign: "center",
          display: "flex",
          justifyContent: "space-around",
          flexWrap: "nowrap",
          marginTop: isSummaryMobile ? 0 : "-20px",
          padding: isSummaryMobile ? "0 4px" : "0px 10px",
          overflowX: "auto",
          width: "100%",
        }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ maxWidth: "100px", textAlign: "center" }}
        >
          <Tooltip title="Eye Opening Response">
            <h2 style={{ fontSize: "20px" }}>E</h2>
          </Tooltip>
          <Statistic
            value={data?.gcs_scales?.eye_opening_response}
            valueStyle={{ fontSize: "18px", whiteSpace: "nowrap" }}
          />
        </Space>
        <Divider type="vertical" />
        <Space
          direction="vertical"
          align="center"
          style={{ maxWidth: "100px", textAlign: "center" }}
        >
          <Tooltip title="Verbal Response">
            <h2 style={{ fontSize: "20px" }}>V</h2>
          </Tooltip>
          <Statistic
            value={data?.gcs_scales?.verbal_response}
            valueStyle={{ fontSize: "18px", whiteSpace: "nowrap" }}
          />
        </Space>
        <Divider type="vertical" />
        <Space
          direction="vertical"
          align="center"
          style={{ maxWidth: "100px", textAlign: "center" }}
        >
          <Tooltip title="Motor Response">
            <h2 style={{ fontSize: "20px" }}>M</h2>
          </Tooltip>
          <Statistic
            value={data?.gcs_scales?.motor_response}
            valueStyle={{ fontSize: "18px", whiteSpace: "nowrap" }}
          />
        </Space>
      </Space>
    </Card>
  );

  return (
    <>
      {noData ? (
        <Card
          style={{ marginTop: "10px" }}
          styles={{
            body: {
              minHeight: "220px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              textAlign: "center",
            },
          }}
        >
          <Image
            src={vitalsIcon}
            width={56}
            height={56}
            preview={false}
            style={{ display: "block" }}
          />
          <h3 style={{ margin: 0, color: "#a3a2a2", fontWeight: "300" }}>
            Vitals not added
          </h3>
        </Card>
      ) : isSummaryMobile ? (
        <Row
          gutter={[6, 6]}
          align="stretch"
          className="landing-graphs-doctor-mobile"
        >
          {graphHeadings.map((item, index) => (
            <Col xs={12} key={index}>
              <CommonGraph
                title={item.heading}
                data={item.graphData}
                lineColor={item.lineColor}
                activeX={activeX}
                onPointChange={setActiveX}
                mobileCompact
              />
            </Col>
          ))}
          <Col xs={12}>{renderIoBalanceCard(true)}</Col>
          <Col xs={12}>{renderGcsCard()}</Col>
          <Col xs={24}>
            <CommonGraph
              title="GRBS"
              data={data?.grbs}
              lineColor="#FD30E8"
              activeX={activeX}
              onPointChange={setActiveX}
              maxY={500}
            />
          </Col>
        </Row>
      ) : (
        <Row gutter={[8, 8]} align="stretch">
          {graphHeadings.map((item, index) => (
            <Col span={8} key={index}>
              <div style={{ paddingTop: 0 }}>
                <CommonGraph
                  title={item.heading}
                  data={item.graphData}
                  lineColor={item.lineColor}
                  activeX={activeX}
                  onPointChange={setActiveX}
                />
              </div>
            </Col>
          ))}
          <Col span={8}>
            <div style={{ paddingTop: 0 }}>{renderIoBalanceCard(false)}</div>
          </Col>
          <Col span={8}>
          <div style={{ paddingTop: "18px" }}>
              <CommonGraph
                title="GRBS"
                data={data?.grbs}
                lineColor="#FD30E8"
                activeX={activeX}
                onPointChange={setActiveX}
                maxY={500}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            {renderGcsCard()}
          </Col>
        </Row>
      )}
    </>
  );
};

export default Graphs;
